import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { StyledButton } from '../../../components';
import Menu from '@mui/material/Menu';
import { getAuditLogs, formatAuditLog } from '../../../services/auditLogger';
import { DataStore, Hub } from 'aws-amplify';
import dayjs from 'dayjs';

interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  entityType: string;
  entityId: string;
  changes: string;
  reason?: string;
}

export default function ReportingLogs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const pageSize = 15;

  // Fetch audit logs from DynamoDB
  useEffect(() => {
    loadAuditLogs();
  }, [dateRange]);

  // Add Hub listener to monitor DataStore sync events for AdminActionHistory
  useEffect(() => {
    const listener = Hub.listen('datastore', ({ payload: { event, data } }) => {
      if (event === 'modelSynced' && data.model.name === 'AdminActionHistory') {
        console.log('AdminActionHistory sync event:', data);
        console.log('Sync counts:', data.counts);
      }
    });

    return () => {
      listener();
    };
  }, []);

  async function loadAuditLogs() {
    try {
      setIsLoading(true);

      // Calculate date range
      let filters: any = {};

      if (dateRange !== 'all') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));

        filters = {
          startDate,
          endDate
        };
      }

      console.log('Fetching audit logs with filters:', filters);
      const logs = await getAuditLogs(filters);
      console.log('Raw audit logs fetched:', logs.length, logs);

      // Format logs for display
      const formattedLogs: AuditLog[] = logs.map((log) => {
        const formatted = formatAuditLog(log);

        // Extract entity type and ID from the action or data
        let entityType = 'Unknown';
        let entityId = 'N/A';

        if (formatted.action.includes('Manifest')) {
          entityType = 'Manifest';
          entityId =
            formatted.after?.manifestId ||
            formatted.before?.manifestId ||
            formatted.after?.id ||
            formatted.before?.id ||
            'N/A';
        } else if (formatted.action.includes('SSCC')) {
          entityType = 'SSCC';
          entityId =
            formatted.after?.ssccId || formatted.before?.ssccId || formatted.after?.id || formatted.before?.id || 'N/A';
        } else if (formatted.action.includes('ASN')) {
          entityType = 'ASN';
          // Handle different ASN action formats
          if (formatted.action === 'ASN Delete') {
            // For bulk ASN delete, show the list of removed ASNs or count
            const removedList =
              formatted.after?.asnsRemovedList || formatted.before?.asnsToBeRemoved?.map((a: any) => a.asn) || [];
            entityId =
              removedList.length > 0
                ? removedList.length <= 3
                  ? removedList.join(', ')
                  : `${removedList.slice(0, 2).join(', ')} +${removedList.length - 2} more`
                : formatted.before?.ssccId || 'N/A';
          } else {
            entityId = formatted.after?.asn || formatted.before?.asn || 'N/A';
          }
        }

        return {
          id: formatted.id,
          timestamp: formatted.timestamp ? dayjs(formatted.timestamp).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
          user: formatted.user || 'Unknown',
          action: formatted.action || 'Unknown Action',
          entityType,
          entityId,
          changes: formatted.changes || 'No changes recorded',
          reason: formatted.reason
        };
      });

      setAuditLogs(formattedLogs);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  }

  const formatActionName = (action: string) => {
    // Shorten common action patterns
    const actionMap: { [key: string]: string } = {
      'SSCC Add to Manifest': 'Add SSCC',
      'SSCC Remove from Manifest': 'Remove SSCC',
      'ASN Remove from SSCC': 'Remove ASN',
      'ASN Assign to SSCC': 'Assign ASN',
      'ASN Unstage': 'Unstage ASN',
      'Manifest Status Change': 'Status Change',
      'Manifest Delete': 'Delete Manifest',
      'Manifest Complete': 'Complete Manifest'
    };

    return actionMap[action] || action;
  };

  const getActionColor = (action: string) => {
    // Return background and text color based on action type
    if (action.includes('Complete') || action.includes('Assign')) {
      return { bg: '#e8f5e9', text: '#2e7d32', border: '#66bb6a' }; // Green for positive actions
    } else if (action.includes('Remove') || action.includes('Delete')) {
      return { bg: '#ffebee', text: '#c62828', border: '#ef5350' }; // Red for destructive actions
    } else if (action.includes('Status Change') || action.includes('Unstage')) {
      return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' }; // Orange for status changes
    } else if (action.includes('Add')) {
      return { bg: '#e3f2fd', text: '#1565c0', border: '#42a5f5' }; // Blue for additions
    } else {
      return { bg: 'rgba(0, 0, 0, 0.03)', text: 'inherit', border: 'rgba(0, 0, 0, 0.08)' }; // Default gray
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = dayjs(timestamp);
    const now = dayjs();
    const diffMinutes = now.diff(date, 'minute');
    const diffHours = now.diff(date, 'hour');
    const diffDays = now.diff(date, 'day');

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.format('MMM D, YYYY h:mm A');
  };

  const getEntityBadgeColor = (entityType: string) => {
    switch (entityType) {
      case 'Manifest':
        return '#1976d2';
      case 'SSCC':
        return '#9c27b0';
      case 'ASN':
        return '#00796b';
      default:
        return '#757575';
    }
  };

  // Filter audit logs based on search and entity filter
  const filteredAuditLogs = auditLogs.filter((log) => {
    // Search filter
    const matchesSearch =
      !searchQuery ||
      log.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.entityId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action.toLowerCase().includes(searchQuery.toLowerCase());

    // Entity filter
    const matchesEntity = entityFilter === 'all' || log.entityType === entityFilter;

    // Debug logging
    if (entityFilter !== 'all') {
      console.log('Filter Debug:', {
        entityFilter,
        logEntityType: log.entityType,
        matchesEntity,
        logAction: log.action,
        logId: log.id
      });
    }

    return matchesSearch && matchesEntity;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredAuditLogs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredAuditLogs.length);
  const paginatedAuditLogs = filteredAuditLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, entityFilter, dateRange]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget as HTMLElement);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'Changes', 'Reason'];
    const csvContent = [
      headers.join(','),
      ...filteredAuditLogs.map((log) =>
        [
          log.timestamp,
          log.user,
          log.action,
          log.entityType,
          log.entityId,
          `"${log.changes.replace(/"/g, '""')}"`,
          `"${(log.reason || 'No reason provided').replace(/"/g, '""')}"`
        ].join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `audit-logs-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`, 'text/csv');
    handleExportMenuClose();
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(filteredAuditLogs, null, 2);
    downloadFile(jsonContent, `audit-logs-${dayjs().format('YYYY-MM-DD-HHmmss')}.json`, 'application/json');
    handleExportMenuClose();
  };

  const downloadFile = (content: string, filename: string, contentType: string) => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Filters */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                Search & Filter
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search by ID, user, or entity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white'
                    }
                  }
                }}
              />
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value)}
                  label="Entity Type"
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white'
                    }
                  }}>
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="Manifest">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1976d2' }} />
                      <span>Manifest</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="SSCC">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#9c27b0' }} />
                      <span>SSCC</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="ASN">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#00796b' }} />
                      <span>ASN</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white'
                    }
                  }}>
                  <MenuItem value="1">Last 24 hours</MenuItem>
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Audit Logs Card */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 2, py: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6" fontWeight={600}>
                  Audit Logs ({filteredAuditLogs.length})
                </Typography>
                {totalPages > 1 && (
                  <Typography variant="body2" color="text.secondary">
                    Page {currentPage} of {totalPages}
                  </Typography>
                )}
              </Stack>
              <Box>
                <Tooltip title="Export audit logs">
                  <Box
                    onClick={filteredAuditLogs.length > 0 ? handleExportMenuOpen : undefined}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: filteredAuditLogs.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0,0,0,0.04)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: filteredAuditLogs.length > 0 ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: filteredAuditLogs.length > 0 ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0,0,0,0.04)'
                      }
                    }}>
                    <DownloadIcon
                      sx={{
                        color: filteredAuditLogs.length > 0 ? '#1976d2' : 'rgba(0,0,0,0.26)',
                        fontSize: 20
                      }}
                    />
                  </Box>
                </Tooltip>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={handleExportMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 1.5
                    }
                  }}>
                  <MenuItem
                    onClick={exportToCSV}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(46, 125, 50, 0.08)'
                      }
                    }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          backgroundColor: 'rgba(46, 125, 50, 0.1)',
                          display: 'flex'
                        }}>
                        <AssessmentIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Export as CSV
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {filteredAuditLogs.length} records • Spreadsheet format
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                  <MenuItem
                    onClick={exportToJSON}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                      }
                    }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          display: 'flex'
                        }}>
                        <AssessmentIcon fontSize="small" sx={{ color: '#1976d2' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Export as JSON
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {filteredAuditLogs.length} records • Raw data format
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredAuditLogs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No audit logs found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery || entityFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Audit logs will appear here when admin actions are performed'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflow: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Timestamp
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        User
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Action
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Entity
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Changes
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Reason
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                          py: 1.5
                        }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAuditLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        sx={{
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                          borderLeft: `3px solid ${getEntityBadgeColor(log.entityType)}`
                        }}>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: '0.65rem' }}>
                            {dayjs(log.timestamp).format('MMM D, h:mm A')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {log.user}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 200,
                              px: 2,
                              py: 0.75,
                              borderRadius: 1,
                              backgroundColor: getActionColor(log.action).bg,
                              border: `1px solid ${getActionColor(log.action).border}`,
                              color: getActionColor(log.action).text
                            }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: getActionColor(log.action).text
                              }}>
                              {formatActionName(log.action)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: getEntityBadgeColor(log.entityType)
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                {log.entityType}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={600} sx={{ pl: 1.5 }}>
                              {log.entityId}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ maxWidth: 300, color: 'text.secondary' }}>
                            {log.changes}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ maxWidth: 250, fontStyle: log.reason ? 'normal' : 'italic' }}>
                            {log.reason || 'No reason provided'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              sx={{ color: 'text.secondary' }}
                              onClick={() => {
                                setSelectedLog(log);
                                setOpenDetailsDialog(true);
                              }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
              <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </Stack>
        </Box>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'white',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <HistoryIcon sx={{ fontSize: 22, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Audit Log Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete record of admin action
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0 }}>
          {selectedLog && (
            <Box>
              {/* Overview Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: 'white' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Overview
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Log ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                      {selectedLog.id}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Timestamp
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      {selectedLog.timestamp}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(selectedLog.timestamp)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Performed By
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                      {selectedLog.user}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Action Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: '#fafafa' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Action Details
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem', mb: 1, display: 'block' }}>
                      Action Type
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        px: 2.5,
                        py: 1,
                        borderRadius: 1.5,
                        backgroundColor: getActionColor(selectedLog.action).bg,
                        border: `1.5px solid ${getActionColor(selectedLog.action).border}`,
                        mt: 1
                      }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: getActionColor(selectedLog.action).text }}>
                        {selectedLog.action}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                      Affected Entity
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: 'white',
                        border: '1px solid rgba(0,0,0,0.08)'
                      }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: getEntityBadgeColor(selectedLog.entityType)
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'text.secondary'
                          }}>
                          {selectedLog.entityType}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                          {selectedLog.entityId}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Changes Section */}
              <Box sx={{ px: 3, py: 3, backgroundColor: 'white' }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 2,
                    display: 'block'
                  }}>
                  Changes Made
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    backgroundColor: '#fafafa',
                    border: '1px solid rgba(0,0,0,0.08)'
                  }}>
                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                    {selectedLog.changes}
                  </Typography>
                </Box>
              </Box>

              {/* Reason Section */}
              {selectedLog.reason && (
                <>
                  <Divider />
                  <Box sx={{ px: 3, py: 3, backgroundColor: '#fafafa' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 2,
                        display: 'block'
                      }}>
                      Reason Provided
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        backgroundColor: 'white',
                        border: '1px solid rgba(0,0,0,0.08)'
                      }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        {selectedLog.reason}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, backgroundColor: '#fafafa', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button
            onClick={() => setOpenDetailsDialog(false)}
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
